/**
 * Crew Type Icons
 * 
 * This file exports all available crew type icons as SVG imports.
 * When adding new icons, add them to src/assets/icons/crew-types/ and import them here.
 */

// Import all crew type icons
import soundIcon from '@/assets/icons/crew-types/sound.svg';
import lightingIcon from '@/assets/icons/crew-types/lighting.svg';
import riggingIcon from '@/assets/icons/crew-types/rigging.svg';
import setIcon from '@/assets/icons/crew-types/set.svg';
import videoIcon from '@/assets/icons/crew-types/video.svg';
import generalIcon from '@/assets/icons/crew-types/general.svg';

/**
 * Map of icon keys to their SVG paths
 * Add new icons here when they're added to the assets folder
 */
export const crewTypeIcons = {
    sound: {
        name: 'Sound',
        icon: soundIcon,
        description: 'Microphone icon for sound/audio crew types'
    },
    lighting: {
        name: 'Lighting',
        icon: lightingIcon,
        description: 'Theatre light icon for lighting crew types'
    },
    rigging: {
        name: 'Rigging',
        icon: riggingIcon,
        description: 'Chain and hook icon for rigging crew types'
    },
    set: {
        name: 'Set',
        icon: setIcon,
        description: 'Saw icon for set/carpentry crew types'
    },
    video: {
        name: 'Video',
        icon: videoIcon,
        description: 'Video camera icon for video crew types'
    },
    general: {
        name: 'General',
        icon: generalIcon,
        description: 'General crew type icon'
    }
};

/**
 * Map API icon values to our icon keys
 * The API might return different icon names (e.g., "lightbulb" instead of "lighting")
 */
const iconMapping = {
    'lightbulb': 'lighting',
    'light': 'lighting',
    'lighting': 'lighting',
    'microphone': 'sound',
    'mic': 'sound',
    'sound': 'sound',
    'audio': 'sound',
    'chain': 'rigging',
    'hook': 'rigging',
    'rigging': 'rigging',
    'rig': 'rigging',
    'saw': 'set',
    'set': 'set',
    'carpentry': 'set',
    'camera': 'video',
    'video': 'video',
    'general': 'general',
    'default': 'general'
};

/**
 * Normalize API icon value to our icon key
 * @param {string} apiIcon - Icon value from API (e.g., "lightbulb")
 * @returns {string} - Normalized icon key (e.g., "lighting")
 */
export const normalizeIconKey = (apiIcon) => {
    if (!apiIcon) return null;
    const normalized = apiIcon.toLowerCase().trim();
    return iconMapping[normalized] || normalized; // Return mapped value or original if no mapping
};

/**
 * Get icon by key (supports both API values and our keys)
 * @param {string} key - Icon key from API or our system (sound, lighting, rigging, set, video, general, or API values like "lightbulb")
 * @returns {string|null} - Icon path or null if not found
 */
export const getCrewTypeIcon = (key) => {
    if (!key) return null;
    const normalizedKey = normalizeIconKey(key);
    return crewTypeIcons[normalizedKey]?.icon || null;
};

/**
 * Get all available icons as an array for dropdowns
 * @returns {Array} - Array of icon objects with key, name, icon, and description
 */
export const getCrewTypeIconsList = () => {
    return Object.entries(crewTypeIcons).map(([key, value]) => ({
        key,
        name: value.name,
        icon: value.icon,
        description: value.description
    }));
};

/**
 * Get icon options for PrimeVue Dropdown
 * @returns {Array} - Array of objects with label and value for dropdown
 */
export const getCrewTypeIconOptions = () => {
    return Object.entries(crewTypeIcons).map(([key, value]) => ({
        label: value.name,
        value: key,
        icon: value.icon
    }));
};

/**
 * Color mapping for crew type icons
 * Each icon has a consistent color that will be used for card backgrounds
 * Colors are chosen to work well with white icons
 */
const iconColors = {
    sound: '#3B82F6',      // Blue - for sound/audio
    lighting: '#F59E0B',   // Amber - for lighting
    rigging: '#8B5CF6',    // Purple - for rigging
    set: '#EF4444',        // Red - for set/carpentry
    video: '#10B981',      // Green - for video
    general: '#6B7280'     // Gray - default/general
};

/**
 * Get consistent color for a crew type icon
 * @param {string} iconKey - Icon key from API or our system (sound, lighting, rigging, set, video, general, or API values like "lightbulb")
 * @returns {string} - Hex color code
 */
export const getCrewTypeColor = (iconKey) => {
    if (!iconKey) return iconColors.general;
    const normalizedKey = normalizeIconKey(iconKey);
    return iconColors[normalizedKey] || iconColors.general;
};

